import cn from 'classnames'
import BaseFlyout from 'ext-ui/components/BaseFlyout/BaseFlyout'
import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement, useEffect, useState } from 'react'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import AssignCardholderDropdown, {
  CardholderSelection,
} from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import { toRecipient } from 'src/app/CardFeedDetails/utils/toRecipient'
import style, {
  cardsCaption,
  cardsContainer,
  description as descriptionStyle,
  flyoutTitle,
  footer,
  header,
  ownerLabel,
  ownerSection,
} from 'src/component-flyout/AssignAccountOwnerFlyout/AssignAccountOwnerFlyout.style'
import AssignAccountOwnerCardsTable from 'src/component-flyout/AssignAccountOwnerFlyout/components/AssignAccountOwnerCardsTable'
import ConfirmCardAssignmentModal, {
  AssignmentRecipient,
} from 'src/component-modal/ConfirmCardAssignmentModal/ConfirmCardAssignmentModal'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import PluralTrns from 'src/localization/PluralTrns'
import { useTranslate } from 'src/localization/Trns'
import { CardFeedCard } from 'src/queries/cardFeeds/cardFeeds.models'
import { Userpilot } from 'userpilot'

export interface AssignAccountOwnerFlyoutProps {
  className?: string
  isOpen: boolean
  onClose: () => void
  cards: CardFeedCard[]
  cardFeedId: string
  /** Called with the number of cards assigned after a successful request. */
  onAssigned?: (assignedCount: number) => void
}

const TRIGGER_ID = 'assign-account-owner-input'

const AssignAccountOwnerFlyout: React.FC<AssignAccountOwnerFlyoutProps> = ({
  className,
  isOpen,
  onClose,
  cards,
  cardFeedId,
  onAssigned,
}): ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const [selected, setSelected] = useState<CardholderSelection | null>(null)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  // Opens the in-app Userpilot resource center (same trigger the Card Feeds
  // "Help Guide" button uses) instead of navigating out to the help center.
  const handleLearnMoreClick = (): void => {
    Userpilot.trigger('resource_center:jNfDAOvnub')
    analytics.track('assign account owner', { action: 'learn more clicked' })
  }

  useEffect(() => {
    if (!isOpen) {
      setSelected(null)
      setIsConfirmOpen(false)
    }
  }, [isOpen])

  const selectedRecipient: AssignmentRecipient | null = selected
    ? toRecipient(selected)
    : null

  const handleClose = (): void => {
    setSelected(null)
    setIsConfirmOpen(false)
    onClose()
  }

  return (
    <>
      <BaseFlyout
        className={cn('AssignAccountOwnerFlyout', className)}
        css={style}
        isOpen={isOpen}
        onClose={handleClose}
        aria-label={translate('Assign Account Owner')}
      >
        {/*
          Title + description rendered inline (not via BaseFlyout's title/
          subtitle props) so the description can contain a HoverableLink —
          BaseFlyout's subtitle slot is string-only.
        */}
        <div css={header}>
          <h2 css={flyoutTitle}>{translate('Assign Account Owner')}</h2>
          <p
            css={descriptionStyle}
            data-testid="assign-account-owner-description"
          >
            {translate(
              'Choose an existing org member or invite someone new. Assigned users will be assigned the Card Manager role in Extend. ${learnMoreLink}',
              {
                learnMoreLink: (
                  <HoverableLink
                    onClick={handleLearnMoreClick}
                    ariaLabelSource={'Learn more about the Card Manager role'}
                    testId={'assign-account-owner-learn-more'}
                    regular
                    underline
                  >
                    {translate('Learn more about the role')}
                  </HoverableLink>
                ),
              },
            )}
          </p>
        </div>
        <div css={ownerSection}>
          <label css={ownerLabel} htmlFor={TRIGGER_ID}>
            {translate('Account Owner')}
          </label>
          <AssignCardholderDropdown
            triggerId={TRIGGER_ID}
            selected={selected}
            onSelect={setSelected}
            placeholder={translate('Select an owner')}
          />
        </div>
        {/*
          Source strings stay sentence-case — the uppercase visual styling
          lives in CSS (cardsCaption text-transform). Shouting in the
          source string is redundant with the CSS, and many target
          languages don't have a sensible all-caps form. Keys are shared
          with CardSelectionSnackbar so translators see one entry.
        */}
        <div
          css={cardsCaption}
          data-testid="assign-account-owner-cards-caption"
        >
          <PluralTrns
            singleSource={'1 card selected'}
            pluralSource={'${count} cards selected'}
            count={cards.length}
          />
        </div>
        <div css={cardsContainer}>
          <AssignAccountOwnerCardsTable cards={cards} />
        </div>
        <div css={footer}>
          <Button
            variant="primary"
            disabled={!selectedRecipient}
            onClick={(): void => setIsConfirmOpen(true)}
            data-testid="assign-account-owner-submit"
          >
            {translate('Assign')}
          </Button>
        </div>
      </BaseFlyout>
      {selectedRecipient && (
        <ConfirmCardAssignmentModal
          isOpen={isConfirmOpen}
          onClose={(): void => setIsConfirmOpen(false)}
          recipient={selectedRecipient}
          cards={cards}
          cardFeedId={cardFeedId}
          onAssigned={(count): void => {
            handleClose()
            onAssigned?.(count)
          }}
        />
      )}
    </>
  )
}

export default AssignAccountOwnerFlyout
